/***
 *
 *   CN
 *   Conditional class joiner with Tailwind conflict resolution.
 *
 *   Ported components follow the reference's shadcn idiom:
 *
 *     cn(buttonStyle({ variant, size }), className)
 *
 *   where a caller's `className` is expected to WIN over the variant's own
 *   classes. A plain string join cannot do that — `h-10 h-9` leaves both in the
 *   class list and Tailwind's own stylesheet order decides, which is usually
 *   the wrong one. So classes are bucketed by the CSS property they set and
 *   the last one in each bucket wins.
 *
 *   This is a deliberately small stand-in for the `tailwind-merge` package,
 *   which we are not adding (see docs/ui-refresh/PLAN.md — "no new deps").
 *   It resolves the property groups this codebase actually uses. Anything it
 *   does not recognise is given its own bucket and therefore always kept, so
 *   an unknown utility is never silently dropped.
 *
 *   If a fidelity bug ever traces back to a conflict this misses, the fix is
 *   to add the group below — or to swap the body for `twMerge(clsx(inputs))`.
 *
 *   USAGE
 *   cn('px-3 py-2', isActive && 'bg-muted', className)
 *
 **********/

// Values that make `text-*` a font-size rather than a colour.
const FONT_SIZES = new Set(['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl', '9xl']);

// Values that make `text-*` an alignment / overflow / transform utility.
const TEXT_KEYWORDS = new Set([
    'left', 'center', 'right', 'justify', 'start', 'end',
    'ellipsis', 'clip', 'wrap', 'nowrap', 'balance', 'pretty',
]);

const BORDER_STYLES = new Set(['solid', 'dashed', 'dotted', 'double', 'hidden', 'none']);
const BORDER_WIDTHS = new Set(['0', '2', '4', '8']); // bare `border` handled separately

// Ordered: first match wins. `null` group means "use the matched prefix".
const GROUPS = [
    // --- layout -----------------------------------------------------------
    [/^(block|inline-block|inline|flex|inline-flex|grid|inline-grid|table|contents|hidden|flow-root|list-item)$/, 'display'],
    [/^(static|fixed|absolute|relative|sticky)$/, 'position'],
    [/^(visible|invisible|collapse)$/, 'visibility'],
    [/^(isolate|isolation-auto)$/, 'isolation'],
    [/^float-/, 'float'],
    [/^clear-/, 'clear'],
    [/^box-(border|content)$/, 'box-sizing'],

    // --- inset ------------------------------------------------------------
    [/^-?inset-x-/, 'inset-x'],
    [/^-?inset-y-/, 'inset-y'],
    [/^-?inset-/, 'inset'],
    [/^-?top-/, 'top'],
    [/^-?right-/, 'right'],
    [/^-?bottom-/, 'bottom'],
    [/^-?left-/, 'left'],
    [/^-?z-/, 'z'],

    // --- flex & grid ------------------------------------------------------
    [/^flex-(row|row-reverse|col|col-reverse)$/, 'flex-direction'],
    [/^flex-(wrap|wrap-reverse|nowrap)$/, 'flex-wrap'],
    [/^flex-/, 'flex'],
    [/^(grow|shrink)(-|$)/, null],
    [/^basis-/, 'basis'],
    [/^-?order-/, 'order'],
    [/^grid-cols-/, 'grid-cols'],
    [/^grid-rows-/, 'grid-rows'],
    [/^grid-flow-/, 'grid-flow'],
    [/^col-span-/, 'col-span'],
    [/^col-start-/, 'col-start'],
    [/^col-end-/, 'col-end'],
    [/^row-span-/, 'row-span'],
    [/^row-start-/, 'row-start'],
    [/^row-end-/, 'row-end'],
    [/^auto-cols-/, 'auto-cols'],
    [/^auto-rows-/, 'auto-rows'],
    [/^gap-x-/, 'gap-x'],
    [/^gap-y-/, 'gap-y'],
    [/^gap-/, 'gap'],
    [/^justify-items-/, 'justify-items'],
    [/^justify-self-/, 'justify-self'],
    [/^justify-/, 'justify-content'],
    [/^content-/, 'align-content'],
    [/^items-/, 'align-items'],
    [/^self-/, 'align-self'],
    [/^place-content-/, 'place-content'],
    [/^place-items-/, 'place-items'],
    [/^place-self-/, 'place-self'],

    // --- spacing ----------------------------------------------------------
    [/^-?p([xytrbl])?-/, (m) => 'p' + (m[1] || '')],
    [/^-?m([xytrbl])?-/, (m) => 'm' + (m[1] || '')],
    [/^-?space-x-/, 'space-x'],
    [/^-?space-y-/, 'space-y'],

    // --- sizing -----------------------------------------------------------
    [/^min-w-/, 'min-w'],
    [/^max-w-/, 'max-w'],
    [/^w-/, 'w'],
    [/^min-h-/, 'min-h'],
    [/^max-h-/, 'max-h'],
    [/^h-/, 'h'],
    [/^size-/, 'size'],
    [/^aspect-/, 'aspect'],

    // --- typography -------------------------------------------------------
    [/^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/, 'font-weight'],
    [/^font-/, 'font-family'],
    [/^-?tracking-/, 'tracking'],
    [/^leading-/, 'leading'],
    [/^(uppercase|lowercase|capitalize|normal-case)$/, 'text-transform'],
    [/^(underline|overline|line-through|no-underline)$/, 'text-decoration'],
    [/^decoration-/, 'text-decoration-color'],
    [/^(italic|not-italic)$/, 'font-style'],
    [/^(truncate)$/, 'text-overflow'],
    [/^line-clamp-/, 'line-clamp'],
    [/^whitespace-/, 'whitespace'],
    [/^break-/, 'word-break'],
    [/^(tabular-nums|proportional-nums|ordinal|slashed-zero|lining-nums|oldstyle-nums|diagonal-fractions|stacked-fractions|normal-nums)$/, 'font-variant-numeric'],
    [/^list-(inside|outside)$/, 'list-position'],
    [/^list-/, 'list-style'],
    [/^align-/, 'vertical-align'],
    // `text-*` is font-size, alignment, overflow, or colour — disambiguate.
    [/^text-/, (m, base) => {
        const v = base.slice(5);
        if (FONT_SIZES.has(v)) return 'font-size';
        if (TEXT_KEYWORDS.has(v)) return 'text-' + v;               // own bucket, never conflated
        if (/^\[/.test(v)) return /^\[(--|#|rgb|hsl|var)/.test(v) ? 'text-color' : 'font-size';
        return 'text-color';
    }],

    // --- backgrounds ------------------------------------------------------
    [/^bg-(fixed|local|scroll)$/, 'bg-attachment'],
    [/^bg-clip-/, 'bg-clip'],
    [/^bg-origin-/, 'bg-origin'],
    [/^bg-(no-repeat|repeat|repeat-x|repeat-y|repeat-round|repeat-space)$/, 'bg-repeat'],
    [/^bg-(auto|cover|contain)$/, 'bg-size'],
    [/^bg-(none|gradient-to-)/, 'bg-image'],
    [/^bg-(bottom|center|left|right|top)/, 'bg-position'],
    [/^bg-/, 'bg-color'],
    [/^from-/, 'gradient-from'],
    [/^via-/, 'gradient-via'],
    [/^to-/, 'gradient-to'],

    // --- borders ----------------------------------------------------------
    [/^rounded-([trbl][lr]?)-/, (m) => 'rounded-' + m[1]],
    [/^rounded-(s|e|ss|se|es|ee)-/, (m) => 'rounded-' + m[1]],
    [/^rounded(-|$)/, 'rounded'],
    [/^border-(collapse|separate)$/, 'border-collapse'],
    [/^border-spacing-/, 'border-spacing'],
    [/^border-([xytrbl])-/, (m) => 'border-w-' + m[1]],
    [/^border-([xytrbl])$/, (m) => 'border-w-' + m[1]],
    [/^border-/, (m, base) => {
        const v = base.slice(7);
        if (BORDER_STYLES.has(v)) return 'border-style';
        if (BORDER_WIDTHS.has(v)) return 'border-w';
        if (/^\[\d/.test(v)) return 'border-w';
        return 'border-color';
    }],
    [/^border$/, 'border-w'],
    [/^divide-x/, 'divide-x'],
    [/^divide-y/, 'divide-y'],
    [/^divide-/, 'divide-color'],
    [/^outline-(none|dashed|dotted|double|solid)$/, 'outline-style'],
    [/^outline-offset-/, 'outline-offset'],
    [/^outline-\d/, 'outline-w'],
    [/^outline$/, 'outline-style'],
    [/^outline-/, 'outline-color'],
    [/^ring-offset-\d/, 'ring-offset-w'],
    [/^ring-offset-/, 'ring-offset-color'],
    [/^ring-(inset|\d)/, 'ring-w'],
    [/^ring$/, 'ring-w'],
    [/^ring-/, 'ring-color'],

    // --- effects ----------------------------------------------------------
    [/^shadow-(inner|none|sm|md|lg|xl|2xl)$/, 'shadow'],
    [/^shadow-\[/, 'shadow'],
    [/^shadow$/, 'shadow'],
    [/^shadow-/, 'shadow-color'],
    [/^opacity-/, 'opacity'],
    [/^mix-blend-/, 'mix-blend'],
    [/^bg-blend-/, 'bg-blend'],
    [/^(blur|brightness|contrast|grayscale|hue-rotate|invert|saturate|sepia|drop-shadow)(-|$)/, (m) => 'filter-' + m[1]],
    [/^backdrop-/, (m, base) => 'backdrop-' + base.split('-')[1]],

    // --- transitions & animation -----------------------------------------
    [/^transition(-|$)/, 'transition'],
    [/^duration-/, 'duration'],
    [/^ease-/, 'ease'],
    [/^delay-/, 'delay'],
    [/^animate-/, 'animate'],

    // --- transforms -------------------------------------------------------
    [/^-?translate-x-/, 'translate-x'],
    [/^-?translate-y-/, 'translate-y'],
    [/^-?scale-x-/, 'scale-x'],
    [/^-?scale-y-/, 'scale-y'],
    [/^-?scale-/, 'scale'],
    [/^-?rotate-/, 'rotate'],
    [/^-?skew-x-/, 'skew-x'],
    [/^-?skew-y-/, 'skew-y'],
    [/^origin-/, 'transform-origin'],
    [/^transform(-|$)/, 'transform'],

    // --- interactivity ----------------------------------------------------
    [/^cursor-/, 'cursor'],
    [/^pointer-events-/, 'pointer-events'],
    [/^select-/, 'user-select'],
    [/^resize(-|$)/, 'resize'],
    [/^appearance-/, 'appearance'],
    [/^touch-/, 'touch-action'],
    [/^snap-(start|end|center|align-none)$/, 'snap-align'],
    [/^snap-(x|y|both|none)$/, 'snap-type'],
    [/^snap-(mandatory|proximity)$/, 'snap-strictness'],
    [/^scroll-m/, 'scroll-m'],
    [/^scroll-p/, 'scroll-p'],
    [/^overscroll-x-/, 'overscroll-x'],
    [/^overscroll-y-/, 'overscroll-y'],
    [/^overscroll-/, 'overscroll'],
    [/^overflow-x-/, 'overflow-x'],
    [/^overflow-y-/, 'overflow-y'],
    [/^overflow-/, 'overflow'],

    // --- svg & tables -----------------------------------------------------
    [/^fill-/, 'fill'],
    [/^stroke-\d/, 'stroke-w'],
    [/^stroke-/, 'stroke'],
    [/^table-/, 'table-layout'],
    [/^object-(contain|cover|fill|none|scale-down)$/, 'object-fit'],
    [/^object-/, 'object-position'],
    [/^columns-/, 'columns'],
    [/^(sr-only|not-sr-only)$/, 'sr-only'],
    [/^(antialiased|subpixel-antialiased)$/, 'font-smoothing'],
];

const cache = new Map();

function groupOf(cls) {
    if (cache.has(cls)) return cache.get(cls);

    // Split leading variants (`hover:`, `md:`, `dark:`, `data-[state=open]:`)
    // from the base utility. Bracketed segments may contain `:`, so scan.
    let depth = 0;
    let lastColon = -1;
    for (let i = 0; i < cls.length; i++) {
        const c = cls[i];
        if (c === '[') depth++;
        else if (c === ']') depth--;
        else if (c === ':' && depth === 0) lastColon = i;
    }

    const variants = lastColon === -1 ? '' : cls.slice(0, lastColon + 1);
    let base = lastColon === -1 ? cls : cls.slice(lastColon + 1);

    // `!` important prefix does not change the property being set.
    if (base.startsWith('!')) base = base.slice(1);

    let group = null;
    for (const [re, g] of GROUPS) {
        const m = base.match(re);
        if (!m) continue;
        group = typeof g === 'function' ? g(m, base) : g === null ? m[0].replace(/-$/, '') : g;
        break;
    }

    // Unrecognised: bucket by the class itself so it is always kept.
    const key = variants + (group === null ? base : group);
    cache.set(cls, key);
    return key;
}

function flatten(inputs, out) {
    for (const input of inputs) {
        if (!input) continue;
        if (typeof input === 'string') out.push(input);
        else if (Array.isArray(input)) flatten(input, out);
        else if (typeof input === 'object') {
            for (const k in input) if (input[k]) out.push(k);
        } else if (typeof input === 'number') out.push(String(input));
    }
    return out;
}

/**
 * Collapse a whitespace-separated class string so the LAST class in each
 * property group wins. Shared with ClassHelper, which builds its string from a
 * `*.tailwind.js` token object and appends the caller's `className` at the end.
 */
export function resolveConflicts(classString) {
    const classes = String(classString || '')
        .split(/\s+/)
        .filter(Boolean);

    const seen = new Map();
    for (const cls of classes) seen.set(groupOf(cls), cls);

    return Array.from(seen.values()).join(' ');
}

export function cn(...inputs) {
    return resolveConflicts(flatten(inputs, []).join(' '));
}

export default cn;
